import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, Loader2, FileText, ExternalLink, CheckCircle2, Download,
  MessageSquare, CalendarClock, LayoutDashboard, Send, Sparkles, Clock,
  Wallet, Lock, CreditCard,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  fetchDashboard, logout, portalDownloadUrl,
  fetchPortalDocuments, fetchPortalMessages, sendPortalMessage,
  fetchPortalBookings, createPortalBooking,
  fetchPortalEngagements, createCheckout,
} from "@/lib/api";

const TABS = [
  { key: "overview",  label: "Overview",  icon: LayoutDashboard },
  { key: "billing",   label: "Billing",   icon: Wallet },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "messages",  label: "Messages",  icon: MessageSquare },
  { key: "bookings",  label: "Bookings",  icon: CalendarClock },
];

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function Portal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    fetchDashboard()
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate]);

  const handleLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    navigate("/login", { replace: true });
  };

  if (loading || !data) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={28} className="text-teal animate-spin" /></div>;
  }

  const { user, cases, resources } = data;
  const primaryCase = cases?.[0];

  return (
    <div data-testid="portal-page" className="bg-sand min-h-[80vh]">
      {/* HEADER */}
      <section className="bg-brand-gradient text-canvas grain relative overflow-hidden">
        <div className="container-pad py-14 relative">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-14 h-14 border-2 border-white/30">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-canvas text-navy font-display">{user?.name?.[0] || "E"}</AvatarFallback>
              </Avatar>
              <div>
                <div className="overline" style={{ color: '#A7E0E6' }}>Client portal</div>
                <h1 className="font-display text-3xl mt-1">Welcome, {user?.name?.split(" ")[0] || "Client"}</h1>
                <div className="text-canvas/70 text-sm mt-1">{user?.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user?.role === "admin" && (
                <Link to="/admin" data-testid="portal-admin-link" className="btn-light-on-navy text-sm">
                  <Sparkles size={14} /> Admin
                </Link>
              )}
              <button data-testid="portal-logout" onClick={handleLogout} className="btn-light-on-navy text-sm">
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>

          {/* TABS */}
          <div className="mt-10 flex flex-wrap gap-2 relative">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`portal-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-all ${
                  tab === t.key
                    ? "bg-canvas text-navy shadow-lg"
                    : "bg-white/10 text-canvas/80 hover:bg-white/20"
                }`}
              >
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PANELS */}
      <section className="container-pad py-12">
        {tab === "overview"  && <OverviewPanel user={user} cases={cases} resources={resources} />}
        {tab === "billing"   && <BillingPanel />}
        {tab === "documents" && <DocumentsPanel />}
        {tab === "messages"  && <MessagesPanel />}
        {tab === "bookings"  && <BookingsPanel primaryCase={primaryCase} />}
      </section>
    </div>
  );
}

/* -------- OVERVIEW -------- */
function OverviewPanel({ user, cases, resources }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
      <div className="md:col-span-8 space-y-6">
        <div className="overline">Active engagements</div>
        {cases.map((c) => (
          <article key={c.case_id} data-testid={`case-${c.case_id}`} className="card-soft p-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-teal">{c.stage}</div>
                <h3 className="font-display text-2xl text-navy mt-2">{c.title}</h3>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl text-navy">{c.progress}%</div>
                <div className="text-xs uppercase tracking-widest text-muted2">Complete</div>
              </div>
            </div>
            <Progress value={c.progress} className="mt-6 h-1.5" />
            <div className="mt-6 flex items-start gap-3 text-sm text-ink">
              <CheckCircle2 size={16} className="text-teal mt-0.5" />
              <span><span className="font-medium">Next:</span> {c.next_action || "Awaiting next milestone update"}</span>
            </div>
            {c.milestones?.length > 0 && (
              <div className="mt-8">
                <div className="overline">Elevate Method progress</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {c.milestones.map((m, idx) => (
                    <div key={m.key} className={`rounded-xl p-3 text-center text-xs transition-colors ${
                      m.completed ? "bg-teal/10 border border-teal/30" : "bg-sand border border-hairline"
                    }`}>
                      <div className={`font-display text-lg ${m.completed ? "text-teal" : "text-navy/40"}`}>{String(idx+1).padStart(2,'0')}</div>
                      <div className={`mt-1 font-medium ${m.completed ? "text-navy" : "text-muted2"}`}>{m.title}</div>
                      {m.completed && <CheckCircle2 size={14} className="text-teal mx-auto mt-2" />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      <aside className="md:col-span-4 space-y-6">
        <div className="card-soft p-8">
          <div className="overline">Your resources</div>
          <ul className="mt-5 space-y-3">
            {resources.map((r) => (
              <li key={r.title}>
                <a href={r.url} target="_blank" rel="noreferrer"
                   className="flex items-center justify-between gap-3 text-sm text-ink hover:text-teal transition-colors py-2 border-b border-hairline last:border-0">
                  <span className="flex items-center gap-2"><FileText size={14} className="text-teal" />{r.title}</span>
                  <ExternalLink size={12} />
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-brand-gradient text-canvas rounded-3xl p-8 relative overflow-hidden grain">
          <div className="overline relative" style={{ color: '#A7E0E6' }}>Need help?</div>
          <p className="text-sm mt-3 text-canvas/80 leading-relaxed relative">
            Send a message from the Messages tab, or email your consultant directly.
          </p>
          <a href="mailto:Jordan@elevateconsultingau.com" className="btn-light-on-navy w-full justify-center mt-5 text-sm relative">
            Email your consultant
          </a>
        </div>

        <div className="card-soft p-6">
          <div className="overline">Reference</div>
          <ul className="mt-4 space-y-2">
            <li>
              <a href="/pricing" data-testid="portal-pricing-link"
                 className="text-sm text-ink hover:text-teal transition-colors inline-flex items-center gap-2">
                <FileText size={14} className="text-teal" /> Pricing & pathways
              </a>
            </li>
          </ul>
        </div>
      </aside>
    </div>
  );
}

/* -------- BILLING -------- */
function BillingPanel() {
  const [engagements, setEngagements] = useState(null);
  const [paying, setPaying] = useState(null);

  const load = useCallback(() => fetchPortalEngagements().then((d) => setEngagements(d.engagements)), []);
  useEffect(() => { load(); }, [load]);

  const payInstallment = async (installment_id) => {
    setPaying(installment_id);
    try {
      const r = await createCheckout(installment_id);
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error("Couldn't start checkout", { description: err?.response?.data?.detail || "Try again" });
      setPaying(null);
    }
  };

  if (!engagements) return <Loader2 size={20} className="text-teal animate-spin" />;

  if (engagements.length === 0) {
    return (
      <div className="card-soft p-16 text-center">
        <Wallet size={40} className="text-teal mx-auto" />
        <h3 className="font-display text-2xl text-navy mt-6">No engagements yet</h3>
        <p className="text-muted2 mt-3 max-w-md mx-auto text-sm">
          Your consultant will scope your engagement in Discovery. When it's ready, your instalments will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {engagements.map((e) => {
        const outstanding = Math.max(0, e.total_amount - (e.amount_paid || 0));
        return (
          <div key={e.engagement_id} className="card-soft p-8" data-testid={`engagement-${e.engagement_id}`}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-7">
                <div className="text-xs uppercase tracking-widest text-teal">{e.package_label}</div>
                <h3 className="font-display text-2xl text-navy mt-2">{e.title}</h3>
                {e.notes && <p className="text-muted2 text-sm mt-3">{e.notes}</p>}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <StatusPill status={e.status} />
                  {e.docs_unlocked
                    ? <span className="inline-flex items-center gap-1 text-teal text-xs bg-teal/10 border border-teal/20 rounded-full px-3 py-1"><CheckCircle2 size={12}/> Documentation unlocked</span>
                    : <span className="inline-flex items-center gap-1 text-navy text-xs bg-sand border border-hairline rounded-full px-3 py-1"><Lock size={12}/> Documentation locked</span>
                  }
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="bg-sand rounded-2xl p-6">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs uppercase tracking-widest text-muted2">Total</span>
                    <span className="font-display text-2xl text-navy">${e.total_amount.toLocaleString("en-AU",{minimumFractionDigits:2})}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-2">
                    <span className="text-xs uppercase tracking-widest text-teal">Paid</span>
                    <span className="font-display text-lg text-teal">${(e.amount_paid || 0).toLocaleString("en-AU",{minimumFractionDigits:2})}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-hairline">
                    <span className="text-xs uppercase tracking-widest text-muted2">Outstanding</span>
                    <span className="font-display text-lg text-navy">${outstanding.toLocaleString("en-AU",{minimumFractionDigits:2})}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="overline">Instalments</div>
              <ul className="mt-4 divide-y divide-hairline">
                {e.installments.map((i) => (
                  <li key={i.installment_id} className="flex items-center justify-between gap-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-display ${
                        i.status === "paid" ? "bg-teal/15 text-teal" :
                        i.status === "waived" ? "bg-sand text-muted2" : "bg-brand-gradient text-canvas"
                      }`}>{i.sequence}</div>
                      <div>
                        <div className="font-medium text-navy">Instalment {i.sequence} of {e.installments_count}</div>
                        <div className="text-xs text-muted2 mt-0.5">${i.amount.toLocaleString("en-AU",{minimumFractionDigits:2})} AUD</div>
                      </div>
                    </div>
                    {i.status === "paid" ? (
                      <span className="inline-flex items-center gap-1 text-teal text-xs"><CheckCircle2 size={14}/> Paid</span>
                    ) : i.status === "waived" ? (
                      <span className="text-xs text-muted2 uppercase tracking-widest">Waived</span>
                    ) : (
                      <button
                        data-testid={`pay-${i.installment_id}`}
                        onClick={() => payInstallment(i.installment_id)}
                        disabled={paying === i.installment_id}
                        className="btn-ochre text-sm !py-2 !px-4 disabled:opacity-60"
                      >
                        {paying === i.installment_id ? <Loader2 size={14} className="animate-spin"/> : <CreditCard size={14}/>}
                        Pay ${i.amount.toLocaleString("en-AU",{minimumFractionDigits:0})}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid_in_full: "bg-teal/10 text-teal border border-teal/20",
    partial:      "bg-yellow-100 text-yellow-800 border border-yellow-200",
    unpaid:       "bg-sand text-navy border border-hairline",
    pending:      "bg-yellow-100 text-yellow-800 border border-yellow-200",
    confirmed:    "bg-teal/10 text-teal border border-teal/20",
    cancelled:    "bg-red-100 text-red-700 border border-red-200",
  };
  return <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${map[status] || map.unpaid}`}>{status?.replace('_',' ')}</span>;
}

/* -------- DOCUMENTS -------- */
function DocumentsPanel() {
  const [state, setState] = useState(null);
  useEffect(() => { fetchPortalDocuments().then(setState); }, []);
  if (!state) return <Loader2 size={20} className="text-teal animate-spin" />;

  if (state.locked) {
    return (
      <div className="card-soft p-12 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-brand-gradient text-canvas flex items-center justify-center"><Lock size={28}/></div>
        <h3 className="font-display text-2xl text-navy mt-6">Documentation locked</h3>
        <p className="text-muted2 mt-4 max-w-md mx-auto text-sm">{state.message}</p>
        {state.outstanding > 0 && (
          <p className="text-muted2 text-sm mt-2">Outstanding balance: <span className="font-display text-navy text-lg">${state.outstanding.toLocaleString("en-AU",{minimumFractionDigits:2})}</span> AUD</p>
        )}
      </div>
    );
  }
  const docs = state.documents;
  return (
    <div className="card-soft p-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Your documents</div>
          <h2 className="font-display text-2xl text-navy mt-3">Shared by your consultant</h2>
        </div>
      </div>
      {docs.length === 0 ? (
        <div className="mt-10 text-center py-14 bg-sand rounded-2xl">
          <FileText size={32} className="text-teal mx-auto" />
          <p className="text-muted2 mt-4 text-sm">No documents shared yet. Your consultant will upload files here when they're ready.</p>
        </div>
      ) : (
        <ul className="mt-8 divide-y divide-hairline">
          {docs.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between gap-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center">
                  <FileText size={16} className="text-teal" />
                </div>
                <div>
                  <div className="font-medium text-navy">{d.original_name}</div>
                  <div className="text-xs text-muted2 mt-0.5">{formatBytes(d.size)} · {formatWhen(d.uploaded_at)}</div>
                </div>
              </div>
              <a href={portalDownloadUrl(d.doc_id)} data-testid={`doc-download-${d.doc_id}`}
                 className="btn-ghost-navy text-sm !py-2 !px-4">
                <Download size={14} /> Download
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------- MESSAGES -------- */
function MessagesPanel() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const r = await fetchPortalMessages();
    setMessages(r.messages);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await sendPortalMessage(text.trim());
      setText("");
      await load();
    } catch { toast.error("Couldn't send message"); }
    setSending(false);
  };

  return (
    <div className="card-soft p-0 flex flex-col h-[70vh] overflow-hidden">
      <div className="p-6 border-b border-hairline">
        <div className="overline">Message thread</div>
        <h2 className="font-display text-xl text-navy mt-2">With your Elevate consultant</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-sand">
        {loading ? (
          <Loader2 size={20} className="text-teal animate-spin mx-auto" />
        ) : messages.length === 0 ? (
          <div className="text-center py-14">
            <MessageSquare size={32} className="text-teal mx-auto" />
            <p className="text-muted2 mt-4 text-sm">Start the conversation with your consultant below.</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role !== "admin";
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl p-4 ${mine ? "bg-navy text-canvas" : "bg-white text-ink border border-hairline"}`}>
                  <div className={`text-[10px] uppercase tracking-widest ${mine ? "text-teal-400" : "text-teal"}`}>
                    {mine ? "You" : m.sender_name || "Consultant"} · {formatWhen(m.created_at)}
                  </div>
                  <div className="mt-2 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="p-4 border-t border-hairline bg-white flex items-end gap-3">
        <Textarea rows={2} data-testid="portal-msg-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Write a message..." className="resize-none flex-1" />
        <button data-testid="portal-msg-send" disabled={sending || !text.trim()} className="btn-ochre !py-3 !px-5 disabled:opacity-60">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
}

/* -------- BOOKINGS -------- */
function BookingsPanel() {
  const [bookings, setBookings] = useState(null);
  const [form, setForm] = useState({ preferred_date: "", preferred_window: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => fetchPortalBookings().then((d) => setBookings(d.bookings)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.preferred_date || !form.preferred_window) return;
    setSubmitting(true);
    try {
      await createPortalBooking(form);
      toast.success("Booking requested — Jordan will confirm soon.");
      setForm({ preferred_date: "", preferred_window: "", notes: "" });
      await load();
    } catch { toast.error("Couldn't submit booking"); }
    setSubmitting(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
      <div className="md:col-span-5">
        <div className="card-soft p-8">
          <div className="overline">Request a session</div>
          <h2 className="font-display text-2xl text-navy mt-3">Book a call with your consultant</h2>
          <p className="text-muted2 text-sm mt-3 leading-relaxed">
            Choose a preferred date and time. Your consultant will confirm and send a meeting link.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs text-muted2">Preferred date</Label>
              <Input type="date" data-testid="booking-date" required value={form.preferred_date}
                     min={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted2">Preferred time</Label>
              <Select value={form.preferred_window} onValueChange={(v) => setForm({ ...form, preferred_window: v })}>
                <SelectTrigger data-testid="booking-window" className="mt-1"><SelectValue placeholder="Pick a window" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Morning (9am to 12pm AEST)">Morning (9am to 12pm AEST)</SelectItem>
                  <SelectItem value="Early afternoon (12pm to 3pm AEST)">Early afternoon (12pm to 3pm AEST)</SelectItem>
                  <SelectItem value="Late afternoon (3pm to 5pm AEST)">Late afternoon (3pm to 5pm AEST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted2">Notes (optional)</Label>
              <Textarea rows={3} data-testid="booking-notes" value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="What would you like to cover?" className="mt-1" />
            </div>
            <button data-testid="booking-submit" disabled={submitting} className="btn-ochre w-full justify-center disabled:opacity-60">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
              Request booking
            </button>
          </form>
        </div>
      </div>
      <div className="md:col-span-7">
        <div className="card-soft p-8">
          <div className="overline">Your bookings</div>
          <h2 className="font-display text-2xl text-navy mt-3">Session history</h2>
          {!bookings ? (
            <Loader2 size={20} className="text-teal animate-spin mt-6" />
          ) : bookings.length === 0 ? (
            <p className="text-muted2 text-sm mt-6">No bookings yet.</p>
          ) : (
            <ul className="mt-6 space-y-3">
              {bookings.map((b) => (
                <li key={b.booking_id} className="bg-sand rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg text-navy">{b.preferred_date}</div>
                      <div className="text-sm text-muted2 mt-0.5">{b.preferred_window}</div>
                      {b.notes && <div className="text-xs text-muted2 mt-2 italic">"{b.notes}"</div>}
                      {b.meeting_link && (
                        <a href={b.meeting_link} target="_blank" rel="noreferrer"
                           className="text-teal text-xs mt-3 inline-flex items-center gap-1 hover:underline">
                          Join meeting <ExternalLink size={10} />
                        </a>
                      )}
                      {b.admin_note && <div className="text-xs text-ink mt-3">Consultant note: {b.admin_note}</div>}
                    </div>
                    <BookingBadge status={b.status} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted2 mt-3 flex items-center gap-1">
                    <Clock size={10} /> Requested {formatWhen(b.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingBadge({ status }) {
  const styles = {
    pending:   "bg-yellow-100 text-yellow-800 border border-yellow-200",
    confirmed: "bg-teal/10 text-teal border border-teal/20",
    cancelled: "bg-red-100 text-red-700 border border-red-200",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}
